import {configureStore} from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";

import countryReducer from "./country/countrySlice";
import categoryReducer from "./category/category-slice";
import usernameReducer from "./username/username-slice";
import userRegisterReducer from "./auth/register/user-register-slice";
import userLoginReducer from "./auth/login/user-login-slice";
import userAccountActivationReducer from "./auth/account-activation/account-activation-slice";
import userResetPasswordReducer from "./auth/forgot-password/forgot-password-slice";
import userProfileReducer from "./profile/profile-slice";
import userServiceReducer from "./service/service-slice";
import userUnavailability from "./unavailability/unavailability-slice";
import userTimezone from "./timezone/timezone-slice";
import publicProfileReducer from "./public-profile/public-profile-slice";
import bookingReducer from "./booking/booking-slice";
import callReducer from "./call/call-slice";
import settingsReducer from "./settings/settings-slice";
import userContextReducer from "./user-context/user-context-slice";
import jwtDecode from "jwt-decode";
import {
  createMigrate,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";

/**
 * A login payload is only usable if it carries a decodable JWT. The 2FA
 * challenge payload (`twoFactorRequired` + `challengeToken`, no `accessToken`)
 * is not, and neither is anything else the API might answer login with.
 */
const hasUsableAccessToken = (userInfo: unknown): boolean => {
  const accessToken = (userInfo as {accessToken?: unknown} | null | undefined)
    ?.accessToken;
  if (typeof accessToken !== "string" || accessToken.length === 0) {
    return false;
  }
  try {
    jwtDecode(accessToken);
    return true;
  } catch {
    return false;
  }
};

/**
 * Heals browsers wedged by the 2FA release that briefly reached prod.
 *
 * While it was live the API answered login with a 2FA challenge instead of a
 * token. The build we rolled back to has no branch for that, so it stored the
 * challenge object as `userInfo` and persisted it here. Those browsers can't
 * log in (every post-login effect gates on `userInfo?.accessToken`) and crash
 * in PrivateRoutes, which used to call jwtDecode on the missing token. The
 * backend DB cleanup can't reach that state — it lives on the user's disk, and
 * until now only clearing site data cleared it.
 *
 * Bumping the persist version runs this once on each user's next load, which
 * does the clearing for them. Sessions with a real token are left signed in.
 */
const authMigrations = {
  1: (state: any) => {
    if (!state) {
      return state;
    }
    // Dropped rather than reset so the reducer's own initial value wins on
    // merge — an explicit `undefined` would survive autoMergeLevel1 and blow
    // up the `twoFactorChallenge.required` read in LoginForm.
    const {twoFactorChallenge, ...rest} = state;
    if (hasUsableAccessToken(rest.userInfo)) {
      return rest;
    }
    return {
      ...rest,
      userInfo: null,
      isError: false,
      isSuccess: false,
      isLoading: false,
      message: "",
    };
  },
};

const persistConfig = {
  key: "root",
  storage,
  version: 1,
  migrate: createMigrate(authMigrations),
  // Request-lifecycle fields have no business surviving a reload: a persisted
  // `isLoading: true` leaves the login button spinning forever, and a
  // persisted challenge re-opens the OTP modal on a cold start.
  whitelist: ["userInfo"],
};
const profilePersistConfig = {
  key: "profile",
  storage,
};
const userContextPersistConfig = {
  key: "userContext",
  storage,
};
const persistedLoginReducer = persistReducer(persistConfig, userLoginReducer);
const persistedProfileReducer = persistReducer(profilePersistConfig, userProfileReducer);
const persistedUserContextReducer = persistReducer(
  userContextPersistConfig,
  userContextReducer
);

export const store = configureStore({
  reducer: {
    country: countryReducer,
    category: categoryReducer,
    username: usernameReducer,
    userLogin: persistedLoginReducer,
    userRegister: userRegisterReducer,
    userAccountActivation: userAccountActivationReducer,
    userResetPassword: userResetPasswordReducer,
    userProfile: persistedProfileReducer,
    userService: userServiceReducer,
    userUnavailability: userUnavailability,
    userTimezone: userTimezone,
    publicProfile: publicProfileReducer,
    booking: bookingReducer,
    userCall: callReducer,
    userSettings: settingsReducer,
    userContext: persistedUserContextReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
